"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Link } from "@/components/common/Link";
import { Mail, Phone, MapPin } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll({
    threshold: 0.1,
    once: true
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll({
    threshold: 0.1,
    once: true
  });
  if (isSubmitted && !submitError) {
    return <div className="min-h-screen flex items-center justify-center bg-light-background px-6">
        <Card className="max-w-md w-full p-8 text-center bg-primary-light border-primary shadow-soft">
          <div className="mb-4 text-primary text-5xl">✓</div>
          <h3 className="text-2xl font-display font-semibold text-foreground-light mb-3">{successMessage}</h3>
          <p className="text-muted-foreground mb-6">We'll get back to you as soon as possible.</p>
          <Button onClick={resetForm} variant="outline" className="w-full">
            Send Another Message
          </Button>
        </Card>
      </div>;
  }
  return <section className="min-h-screen bg-light-background">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 min-h-screen">
        {}
        <div ref={leftRef} className="flex items-center justify-center px-6 py-16 lg:px-12">
          <Card className="w-full max-w-lg bg-background border-border-strong shadow-medium p-8">
            <div className="mb-8">
              <h2 className="text-3xl font-display font-bold text-foreground-light mb-2" style={{
              opacity: leftVisible ? 1 : 0,
              transform: leftVisible ? 'translateY(0)' : 'translateY(1rem)',
              transition: 'opacity 0.15s ease-out, transform 0.15s ease-out'
            }}>
                Get In Touch
              </h2>
              <p className="text-muted-foreground" style={{
              opacity: leftVisible ? 1 : 0,
              transform: leftVisible ? 'translateY(0)' : 'translateY(1rem)',
              transition: 'opacity 0.15s ease-out 0.05s, transform 0.15s ease-out 0.05s'
            }}>
                We'd love to hear from you. Send us a message and we'll respond as soon as possible.
              </p>
            </div>

            <Form onSubmit={handleSubmit}>
              <div className="space-y-5">
                <div style={{
                opacity: leftVisible ? 1 : 0,
                transform: leftVisible ? 'translateY(0)' : 'translateY(1rem)',
                transition: 'opacity 0.15s ease-out 0.1s, transform 0.15s ease-out 0.1s'
              }}>
                  <FormField control={control} name="name" rules={{
                  required: "Name is required",
                  minLength: {
                    value: 2,
                    message: "Name must be at least 2 characters"
                  }
                }} render={({
                  field
                }) => <FormItem>
                        <FormLabel className="text-foreground-light font-semibold">Full Name</FormLabel>
                        <FormControl>
                          <input className="w-full px-4 py-2.5 rounded-md border border-border bg-background text-foreground-light focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-200" placeholder="Your name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                </div>

                <div style={{
                opacity: leftVisible ? 1 : 0,
                transform: leftVisible ? 'translateY(0)' : 'translateY(1rem)',
                transition: 'opacity 0.15s ease-out 0.25s, transform 0.15s ease-out 0.25s'
              }}>
                  <FormField control={control} name="email" rules={{
                  required: "Email is required",
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Please enter a valid email"
                  }
                }} render={({
                  field
                }) => <FormItem>
                        <FormLabel className="text-foreground-light font-semibold">Email Address</FormLabel>
                        <FormControl>
                          <input type="email" className="w-full px-4 py-2.5 rounded-md border border-border bg-background text-foreground-light focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-200" placeholder="you@example.com" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                </div>

                <div style={{
                opacity: leftVisible ? 1 : 0,
                transform: leftVisible ? 'translateY(0)' : 'translateY(1rem)',
                transition: 'opacity 0.15s ease-out 0.4s, transform 0.15s ease-out 0.4s'
              }}>
                  <FormField control={control} name="subject" rules={{
                  required: "Subject is required",
                  minLength: {
                    value: 3,
                    message: "Subject must be at least 3 characters"
                  }
                }} render={({
                  field
                }) => <FormItem>
                        <FormLabel className="text-foreground-light font-semibold">Subject</FormLabel>
                        <FormControl>
                          <input className="w-full px-4 py-2.5 rounded-md border border-border bg-background text-foreground-light focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-200" placeholder="How can we help?" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                </div>

                <div style={{
                opacity: leftVisible ? 1 : 0,
                transform: leftVisible ? 'translateY(0)' : 'translateY(1rem)',
                transition: 'opacity 0.15s ease-out 0.55s, transform 0.15s ease-out 0.55s'
              }}>
                  <FormField control={control} name="message" rules={{
                  required: "Message is required",
                  minLength: {
                    value: 10,
                    message: "Message must be at least 10 characters"
                  }
                }} render={({
                  field
                }) => <FormItem>
                        <FormLabel className="text-foreground-light font-semibold">Message</FormLabel>
                        <FormControl>
                          <textarea className="w-full px-4 py-2.5 rounded-md border border-border bg-background text-foreground-light focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-200 min-h-32 resize-y" placeholder="Tell us more about your inquiry..." {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                </div>

                {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-md" style={{
                opacity: leftVisible ? 1 : 0,
                transform: leftVisible ? 'translateY(0)' : 'translateY(1rem)',
                transition: 'opacity 0.15s ease-out 0.7s, transform 0.15s ease-out 0.7s'
              }}>
                    <p className="text-red-800 text-sm">{submitError}</p>
                  </div>}

                <div style={{
                opacity: leftVisible ? 1 : 0,
                transform: leftVisible ? 'translateY(0)' : 'translateY(1rem)',
                transition: 'opacity 0.15s ease-out 0.7s, transform 0.15s ease-out 0.7s'
              }}>
                  <Button type="submit" disabled={isSubmitting} className="w-full bg-primary text-foreground-primary hover:bg-primary-hover hover:shadow-medium hover:-translate-y-0.5 active:bg-primary-active active:translate-y-0 transition-all duration-200 ease-out py-3 font-semibold">
                    {isSubmitting ? <>
                        <span className="inline-block animate-spin mr-2">⏳</span>
                        Sending...
                      </> : 'Send Message'}
                  </Button>
                </div>
              </div>
            </Form>
          </Card>
        </div>

        {}
        <div ref={rightRef} className="flex items-center justify-center px-6 py-16 lg:px-12 bg-dark-background">
          <div className="w-full max-w-lg space-y-8">
            <div>
              <h3 className="text-2xl font-display font-bold text-foreground-dark mb-3" style={{
              opacity: rightVisible ? 1 : 0,
              transform: rightVisible ? 'translateX(0)' : 'translateX(2rem)',
              transition: 'opacity 0.3s ease-out, transform 0.3s ease-out'
            }}>
                Contact Information
              </h3>
              <p className="text-foreground-dark/80" style={{
              opacity: rightVisible ? 1 : 0,
              transform: rightVisible ? 'translateX(0)' : 'translateX(2rem)',
              transition: 'opacity 0.3s ease-out 0.1s, transform 0.3s ease-out 0.1s'
            }}>
                Reach out to Haven Holdings Capital for investment inquiries and partnership opportunities.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-4" style={{
              opacity: rightVisible ? 1 : 0,
              transform: rightVisible ? 'translateX(0)' : 'translateX(2rem)',
              transition: 'opacity 0.3s ease-out 0.2s, transform 0.3s ease-out 0.2s'
            }}>
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center">
                  <Mail className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h4 className="text-foreground-dark font-semibold mb-1">Email</h4>
                  <Link to="mailto:info@havenholdingscapital.com" className="text-foreground-dark/80 hover:text-primary transition-colors duration-200">
                    info@havenholdingscapital.com
                  </Link>
                </div>
              </div>

              <div className="flex items-start gap-4" style={{
              opacity: rightVisible ? 1 : 0,
              transform: rightVisible ? 'translateX(0)' : 'translateX(2rem)',
              transition: 'opacity 0.3s ease-out 0.3s, transform 0.3s ease-out 0.3s'
            }}>
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-secondary/20 flex items-center justify-center">
                  <Phone className="w-6 h-6 text-secondary" />
                </div>
                <div>
                  <h4 className="text-foreground-dark font-semibold mb-1">Phone</h4>
                  <p className="text-foreground-dark/80">Available via email inquiry</p>
                </div>
              </div>

              <div className="flex items-start gap-4" style={{
              opacity: rightVisible ? 1 : 0,
              transform: rightVisible ? 'translateX(0)' : 'translateX(2rem)',
              transition: 'opacity 0.3s ease-out 0.4s, transform 0.3s ease-out 0.4s'
            }}>
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-accent/20 flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <h4 className="text-foreground-dark font-semibold mb-1">Office</h4>
                  <p className="text-foreground-dark/80">Serving nationwide investment opportunities</p>
                </div>
              </div>
            </div>

            <div className="pt-8 border-t border-foreground-dark/20" style={{
            opacity: rightVisible ? 1 : 0,
            transform: rightVisible ? 'translateX(0)' : 'translateX(2rem)',
            transition: 'opacity 0.3s ease-out 0.5s, transform 0.3s ease-out 0.5s'
          }}>
              <p className="text-foreground-dark/70 text-sm leading-relaxed">
                Haven Holdings Capital specializes in developing turn-key, purpose-built properties for triple-net tenants in highly regulated industries. We focus on disciplined underwriting and risk-adjusted returns.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
export default function ContactForm() {
  return <FormProvider formId="contact-form-main" sectionName="contact-section" defaultValues={{
    name: "",
    email: "",
    subject: "",
    message: ""
  }} validationRules={{
    name: {
      required: true,
      minLength: 2,
      maxLength: 50
    },
    email: {
      required: true,
      pattern: "email"
    },
    subject: {
      required: true,
      minLength: 3
    },
    message: {
      required: true,
      minLength: 10
    }
  }} submitText="Send Message" successMessage="Thank you for reaching out! We'll respond to your inquiry shortly." errorMessage="We couldn't send your message. Please try again or email us directly.">
      <ContactFormContent />
    </FormProvider>;
}